import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  updateDoc, 
  orderBy, 
  Timestamp, 
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';

/**
 * Service for managing payment proofs and transactions
 */
const paymentProofService = {
  /**
   * Get all payment transactions for a specific brand
   * @param {string} brandId - Brand ID
   * @returns {Promise<Array>} Array of payment transactions
   */
  getBrandPayments: async (brandId) => {
    try {
      const campaignsQuery = query(
        collection(db, 'campaigns'),
        where('brandId', '==', brandId),
        orderBy('createdAt', 'desc')
      );
      
      const campaignsSnapshot = await getDocs(campaignsQuery);
      
      const payments = [];
      campaignsSnapshot.forEach((doc) => {
        const campaignData = doc.data();
        
        // Only include campaigns that have payment information
        if (campaignData.payment) {
          payments.push({
            id: doc.id,
            campaignName: campaignData.name,
            amount: campaignData.payment.commission,
            status: campaignData.status,
            paymentDate: campaignData.payment.verifiedAt,
            proofUrl: campaignData.payment.proofUrl,
            proofUploadedAt: campaignData.payment.proofUploadedAt,
            referenceCode: campaignData.payment.referenceCode,
            createdAt: campaignData.createdAt,
          });
        }
      });
      
      return payments;
    } catch (error) {
      console.error('Error getting brand payments:', error);
      throw error;
    }
  },
  
  /**
   * Upload payment proof for a campaign
   * @param {string} campaignId - Campaign ID
   * @param {File} file - Payment proof file
   * @param {string} referenceCode - Payment reference code
   * @returns {Promise<Object>} Updated campaign data
   */
  uploadPaymentProof: async (campaignId, file, referenceCode) => {
    try {
      // 1. Upload file to Firebase Storage
      const fileRef = ref(storage, `payment_proofs/${campaignId}_${file.name}`);
      await uploadBytes(fileRef, file);
      
      // 2. Get the download URL
      const downloadURL = await getDownloadURL(fileRef);
      
      // 3. Update the campaign document with the proof URL
      const campaignRef = doc(db, 'campaigns', campaignId);
      
      await updateDoc(campaignRef, {
        'payment.proofUrl': downloadURL,
        'payment.proofUploadedAt': serverTimestamp(),
        'payment.referenceCode': referenceCode,
        status: 'proof_submitted', // Update the main campaign status field
        updatedAt: serverTimestamp()
      });
      
      // 4. Get the updated campaign data
      const updatedCampaign = await getDoc(campaignRef);
      
      return {
        id: updatedCampaign.id,
        ...updatedCampaign.data()
      };
    } catch (error) {
      console.error('Error uploading payment proof:', error);
      throw error;
    }
  },
  
  /**
   * Get a specific campaign payment by ID
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Campaign payment data
   */
  getPaymentById: async (campaignId) => {
    try {
      const docRef = doc(db, 'campaigns', campaignId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const campaignData = docSnap.data();
        return {
          id: docSnap.id,
          campaignName: campaignData.name,
          amount: campaignData.payment?.commission,
          status: campaignData.status,
          paymentDate: campaignData.payment?.verifiedAt,
          proofUrl: campaignData.payment?.proofUrl,
          proofUploadedAt: campaignData.payment?.proofUploadedAt,
          referenceCode: campaignData.payment?.referenceCode,
          createdAt: campaignData.createdAt,
        };
      } else {
        throw new Error('Campaign not found');
      }
    } catch (error) {
      console.error('Error getting payment:', error);
      throw error;
    }
  },
  
  /**
   * Generate a reference code for a campaign
   * @param {string} campaignId - Campaign ID
   * @returns {string} Reference code
   */
  generateReferenceCode: (campaignId) => {
    if (!campaignId) return '';
    
    // Format: EM-YYYY-NNNN where YYYY is current year and NNNN is a numeric part based on campaign ID
    const currentYear = new Date().getFullYear();
    
    // Convert the first 4 chars of campaign ID to a numeric value
    let numericPart = '';
    for (let i = 0; i < 4 && i < campaignId.length; i++) {
      // Get character code and use modulo to ensure it's 0-9
      const charCode = campaignId.charCodeAt(i) % 10;
      numericPart += charCode;
    }
    
    // Pad with zeros if needed
    while (numericPart.length < 4) {
      numericPart = '0' + numericPart;
    }
    
    return `EM-${currentYear}-${numericPart}`;
  }
};

export default paymentProofService;
